import { getNumberLocale } from '../../../lib/base-i18n';
import './UsageChart.css';

/**
 * 사용량 그래프 — 판 L (2026-09-10 사용자 지시: 「그래프로 나와라」).
 *
 * ## ★★★ 라이브러리를 안 쓴다
 *
 * 필요한 것은 «막대 둘»이다 — 기간별 추이(세로)와 축별 순위(가로). 차트 라이브러리는
 * 번들에 수백 KB 를 얹고 테마 토큰과 따로 논다. 여기서는 토큰만 쓴 CSS 막대로 그려
 * 색이 다섯 테마를 그대로 따라가게 한다.
 *
 * ## ★★ 「0」과 「기록 없음」을 가른다
 *
 * 줄이 없으면 **그리지 않는다.** 빈 축에 0 짜리 막대를 세우면 「안 썼다」와 「기록이 없다」가
 * 같은 그림이 된다.
 *
 * ## ★ 가장 큰 것이 100% 다
 *
 * 절대 크기가 아니라 «서로 견주는» 그림이다 — 눈금 숫자를 함께 적어 그 사실을 말한다.
 */

export interface UsagePoint {
  bucket: string;
  tokens: number;
  calls: number;
}

const fmt = (n: number): string => n.toLocaleString(getNumberLocale());

/** 기간별 추이 — 세로 막대. */
export function UsageTrend({ points, unit }: { points: UsagePoint[]; unit: string }) {
  if (points.length === 0) {
    return <p className="uchart__none">아직 기록이 없습니다.</p>;
  }
  const max = points.reduce((m, p) => Math.max(m, p.tokens), 0);
  return (
    <div className="uchart" role="img" aria-label={`${unit} 사용량 추이 — ${points.length}개 구간`}>
      <div className="uchart__bars">
        {points.map((p) => {
          const h = max > 0 ? Math.max(2, Math.round((p.tokens / max) * 100)) : 0;
          return (
            <div key={p.bucket} className="uchart__col" title={`${p.bucket} — ${fmt(p.tokens)} 토큰 · ${p.calls}회`}>
              <span className="uchart__bar" style={{ height: `${h}%` }} />
              <span className="uchart__lbl">{p.bucket}</span>
            </div>
          );
        })}
      </div>
      {/* ★ 눈금은 «가장 큰 값»이다 — 안 적으면 막대 높이가 무슨 뜻인지 알 수 없다. */}
      <p className="uchart__scale">가장 높은 구간 {fmt(max)} 토큰</p>
    </div>
  );
}

export interface UsageRank {
  key: string;
  label: string;
  promptTokens: number;
  completionTokens: number;
  calls: number;
}

/** 축별 순위 — 가로 막대. */
export function UsageRanks({
  title, rows, note, max: maxRows = 10,
}: { title: string; rows: UsageRank[]; note?: string; max?: number }) {
  const shown = rows.slice(0, maxRows);
  const max = shown.reduce((m, r) => Math.max(m, r.promptTokens + r.completionTokens), 0);
  return (
    <section className="urank">
      <h4 className="urank__h">{title}</h4>
      {note !== undefined && <p className="urank__note">{note}</p>}
      {shown.length === 0 ? (
        /* ★ 0 짜리 막대를 세우지 않는다 — 「안 썼다」와 「기록이 없다」는 다른 사실이다. */
        <p className="uchart__none">아직 기록이 없습니다.</p>
      ) : (
        <ul className="urank__list">
          {shown.map((r) => {
            const t = r.promptTokens + r.completionTokens;
            const w = max > 0 ? Math.max(2, Math.round((t / max) * 100)) : 0;
            return (
              <li key={r.key || '(none)'} className="urank__row">
                <span className="urank__nm" title={r.label}>{r.label}</span>
                <span className="urank__track"><span className="urank__fill" style={{ width: `${w}%` }} /></span>
                <span className="urank__v">{fmt(t)}</span>
                <span className="urank__c">{fmt(r.calls)}회</span>
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > shown.length && (
        <p className="urank__note">많이 쓴 {shown.length}개만 보입니다 (전체 {rows.length}개).</p>
      )}
    </section>
  );
}
